/**
 * pixi-particles — EmitterOp.ts
 *
 * Ported from Phaser's `Phaser.GameObjects.Particles.EmitterOp` (MIT licensed,
 * (c) Phaser Studio Inc). Logic is unchanged; only its dependencies have been
 * swapped for the standalone helpers in Utils.ts / Easing.ts / Interpolation.ts.
 *
 * Takes control over a single property on a Particle (x, y, alpha, scaleX,
 * lifespan, ...) and manages how its value is derived both at the moment a
 * particle is emitted (`onEmit`) and every subsequent update (`onUpdate`),
 * based on how the property was configured — see EmitterConfig.ts for the
 * full set of accepted shapes.
 */

import { Between, Clamp, FloatBetween, GetFastValue, SnapTo, Wrap } from './Utils';
import { GetEaseFunction, EaseFn } from './Easing';
import { GetInterpolationFunction, InterpolationFn } from './Interpolation';

// Kept loose intentionally: a Particle needs to reference EmitterOp and vice
// versa, so a strict circular type would add ceremony without real safety.
export type AnyParticle = any;

export class EmitterOp
{
    propertyKey: string;
    propertyValue: any;
    defaultValue: any;

    steps = 0;
    counter = 0;
    yoyo = false;
    direction = 0;

    start: any = 0;
    current: any = 0;
    end = 0;

    ease: EaseFn | null = null;
    interpolation: InterpolationFn | null = null;

    emitOnly: boolean;

    onEmit: (particle?: AnyParticle, key?: string, value?: any) => any;
    onUpdate: (particle: AnyParticle, key: string, t: number, value: any) => any;

    active = true;
    method = 0;

    protected _onEmit?: Function;
    protected _onUpdate?: Function;

    constructor (key: string, defaultValue: any, emitOnly = false)
    {
        this.propertyKey = key;
        this.propertyValue = defaultValue;
        this.defaultValue = defaultValue;
        this.emitOnly = emitOnly;

        this.onEmit = this.defaultEmit;
        this.onUpdate = this.defaultUpdate;
    }

    loadConfig (config: any = {}, newKey?: string): void
    {
        if (newKey)
        {
            this.propertyKey = newKey;
        }

        this.propertyValue = GetFastValue(config, this.propertyKey, this.defaultValue);

        this.method = this.getMethod();

        this.setMethods();

        if (this.emitOnly)
        {
            this.onUpdate = this.defaultUpdate;
        }
    }

    toJSON (): string
    {
        return JSON.stringify(this.propertyValue);
    }

    onChange (value: any): this
    {
        var current: any;

        switch (this.method)
        {
            case 1:
            case 3:
            case 8:
                current = value;
                break;

            case 2:
                if (this.propertyValue.indexOf(value) >= 0)
                {
                    current = value;
                }
                break;

            case 4:
                var step = (this.end - this.start) / this.steps;
                current = SnapTo(value, step);
                this.counter = current;
                break;

            case 5:
            case 6:
            case 7:
                current = Clamp(value, this.start, this.end);
                break;

            case 9:
                current = this.start[0];
                break;
        }

        this.current = current;

        return this;
    }

    getMethod (): number
    {
        var value = this.propertyValue;

        if (value === null)
        {
            return 0;
        }

        var t = typeof value;

        if (t === 'number')
        {
            return 1;
        }
        else if (Array.isArray(value))
        {
            return 2;
        }
        else if (t === 'function')
        {
            return 3;
        }
        else if (t === 'object')
        {
            if (this.hasBoth(value, 'start', 'end'))
            {
                return this.has(value, 'steps') ? 4 : 5;
            }
            else if (this.hasBoth(value, 'min', 'max'))
            {
                return 6;
            }
            else if (this.has(value, 'random'))
            {
                return 7;
            }
            else if (this.hasEither(value, 'onEmit', 'onUpdate'))
            {
                return 8;
            }
            else if (this.hasEither(value, 'values', 'interpolation'))
            {
                return 9;
            }
        }

        return 0;
    }

    setMethods (): this
    {
        var value = this.propertyValue;
        var current = value;

        var onEmit: any = this.defaultEmit;
        var onUpdate: any = this.defaultUpdate;

        switch (this.method)
        {
            case 1:
                onEmit = this.staticValueEmit;
                break;

            case 2:
                onEmit = this.randomStaticValueEmit;
                current = value[0];
                break;

            case 3:
                this._onEmit = value;
                onEmit = this.proxyEmit;
                current = this.defaultValue;
                break;

            case 4:
                this.start = value.start;
                this.end = value.end;
                this.steps = value.steps;
                this.counter = this.start;
                this.yoyo = this.has(value, 'yoyo') ? value.yoyo : false;
                this.direction = 0;
                onEmit = this.steppedEmit;
                current = this.start;
                break;

            case 5:
                this.start = value.start;
                this.end = value.end;
                var easeType = this.has(value, 'ease') ? value.ease : 'Linear';
                this.ease = GetEaseFunction(easeType, value.easeParams);
                onEmit = (this.has(value, 'random') && value.random) ? this.randomRangedValueEmit : this.easedValueEmit;
                onUpdate = this.easeValueUpdate;
                current = this.start;
                break;

            case 6:
                this.start = value.min;
                this.end = value.max;
                onEmit = (this.has(value, 'int') && value.int) ? this.randomRangedIntEmit : this.randomRangedValueEmit;
                current = this.start;
                break;

            case 7:
                var rnd = value.random;

                if (Array.isArray(rnd))
                {
                    this.start = rnd[0];
                    this.end = rnd[1];
                }

                onEmit = this.randomRangedIntEmit;
                current = this.start;
                break;

            case 8:
                this._onEmit = this.has(value, 'onEmit') ? value.onEmit : this.defaultEmit;
                this._onUpdate = this.has(value, 'onUpdate') ? value.onUpdate : this.defaultUpdate;
                onEmit = this.proxyEmit;
                onUpdate = this.proxyUpdate;
                current = this.defaultValue;
                break;

            case 9:
                this.start = value.values;
                var easeTypeI = this.has(value, 'ease') ? value.ease : 'Linear';
                this.ease = GetEaseFunction(easeTypeI, value.easeParams);
                this.interpolation = GetInterpolationFunction(value.interpolation);
                onEmit = this.easedValueEmit;
                onUpdate = this.easeValueUpdate;
                current = this.start[0];
                break;
        }

        this.onEmit = onEmit.bind(this);
        this.onUpdate = onUpdate.bind(this);
        this.current = current;

        return this;
    }

    protected has (object: any, key: string): boolean
    {
        return Object.prototype.hasOwnProperty.call(object, key);
    }

    protected hasBoth (object: any, key1: string, key2: string): boolean
    {
        return this.has(object, key1) && this.has(object, key2);
    }

    protected hasEither (object: any, key1: string, key2: string): boolean
    {
        return this.has(object, key1) || this.has(object, key2);
    }

    defaultEmit (): any
    {
        return this.defaultValue;
    }

    defaultUpdate (_particle: AnyParticle, _key: string, _t: number, value: any): any
    {
        return value;
    }

    protected proxyEmit (particle: AnyParticle, key: string, value?: any): any
    {
        var result = this._onEmit!(particle, key, value);

        this.current = result;

        return result;
    }

    protected proxyUpdate (particle: AnyParticle, key: string, t: number, value: any): any
    {
        var result = this._onUpdate!(particle, key, t, value);

        this.current = result;

        return result;
    }

    protected staticValueEmit (): any
    {
        return this.current;
    }

    protected staticValueUpdate (): any
    {
        return this.current;
    }

    protected randomStaticValueEmit (): any
    {
        var randomIndex = Math.floor(Math.random() * this.propertyValue.length);

        this.current = this.propertyValue[randomIndex];

        return this.current;
    }

    protected randomRangedValueEmit (particle: AnyParticle, key: string): number
    {
        var value = FloatBetween(this.start, this.end);

        if (particle && particle.data[key])
        {
            particle.data[key].min = value;
            particle.data[key].max = this.end;
        }

        this.current = value;

        return value;
    }

    protected randomRangedIntEmit (particle: AnyParticle, key: string): number
    {
        var value = Between(this.start, this.end);

        if (particle && particle.data[key])
        {
            particle.data[key].min = value;
            particle.data[key].max = this.end;
        }

        this.current = value;

        return value;
    }

    protected steppedEmit (): any
    {
        var current = this.counter;
        var next = current;
        var step = (this.end - this.start) / this.steps;

        if (this.yoyo)
        {
            var over: number;

            if (this.direction === 0)
            {
                next += step;

                if (next >= this.end)
                {
                    over = next - this.end;
                    next = this.end - over;
                    this.direction = 1;
                }
            }
            else
            {
                next -= step;

                if (next <= this.start)
                {
                    over = this.start - next;
                    next = this.start + over;
                    this.direction = 0;
                }
            }

            this.counter = next;
        }
        else
        {
            this.counter = Wrap(next + step, this.start, this.end);
        }

        this.current = current;

        return current;
    }

    protected easedValueEmit (particle?: AnyParticle, key?: string): any
    {
        if (particle && key && particle.data[key])
        {
            var data = particle.data[key];

            data.min = this.start;
            data.max = this.end;
        }

        this.current = this.start;

        return this.start;
    }

    protected easeValueUpdate (particle: AnyParticle, key: string, t: number): any
    {
        var data = particle.data[key];

        var current: any;
        var v = this.ease!(t);

        if (this.interpolation)
        {
            current = this.interpolation(this.start, v);
        }
        else
        {
            current = (data.max - data.min) * v + data.min;
        }

        this.current = current;

        return current;
    }

    destroy (): void
    {
        this.propertyValue = null;
        this.defaultValue = null;
        this.ease = null;
        this.interpolation = null;
        this._onEmit = undefined;
        this._onUpdate = undefined;
    }
}

export default EmitterOp;
